import React from 'react'
import { CustomType, GoalId, GoalWithProgress, ProgressType } from '../../services/api.types'
import YumGoalRankingWrapper from '../../components/YumGoalRanking/YumGoalRankingWrapper'
import YumCustomEntryWrapper from '../../components/YumCustomEntry/YumCustomEntryWrapper'
import NotificationsWrapper from '../../components/Notification/NotificationsWrapper'
import YumUserProgressCelebration from '../../components/YumUserProgressCelebration/YumUserProgressCelebration'
import { isNative } from '../../common/helpers/isNative'
import { t } from 'ttag'
import { App } from '@capacitor/app'
import { PluginListenerHandle } from '@capacitor/core'
import { store } from '../../index'
import { push } from 'connected-react-router'
import { syncChallengeProgress } from '../../common/helpers/challengeProgressSync'
import { ActivityView } from '../Activity/store/ActivityReducer'

// Types
export interface IChallengeRankingProps {
  goalId: GoalId
  token?: string
  goal?: GoalWithProgress
  isLegacyRanking: boolean
  activityView: ActivityView
  getCurrentCompanyGoal({ id, token }: { id: GoalId; token?: string }): void
  cancelGetChallengesWithProgress(): void
}

// Utility functions
const isChallengeTypeSupported = (goal: GoalWithProgress): boolean => {
  return (
    Object.values(ProgressType).includes(goal.progressType) &&
    (!goal.customType || Object.values(CustomType).includes(goal.customType))
  )
}

// Hooks
const useAppStateListener = (
  goalId: GoalId,
  token?: string,
  getCurrentCompanyGoal?: ({ id, token }: { id: GoalId; token?: string }) => void,
) => {
  React.useEffect(() => {
    let appStateChangeListener: PluginListenerHandle | undefined

    const setListener = async () => {
      if (!getCurrentCompanyGoal) return

      appStateChangeListener = await App.addListener('appStateChange', async ({ isActive }) => {
        if (isActive) {
          getCurrentCompanyGoal({ id: goalId, token })
        }
      })
    }

    setListener()

    return () => {
      if (appStateChangeListener) {
        try {
          appStateChangeListener.remove()
        } catch (error) {
          // Ignore cleanup errors
        }
      }
    }
  }, [goalId, token, getCurrentCompanyGoal])
}

// Components
const UnsupportedChallengeMessage = () => (
  <div className={'text-center'}>{t`Challenge type not supported. Please check for updates.`}</div>
)

export default function ChallengeRanking(props: IChallengeRankingProps) {
  const { goalId, goal, isLegacyRanking, token, activityView, getCurrentCompanyGoal, cancelGetChallengesWithProgress } =
    props

  // Initial data fetch
  React.useEffect(() => {
    // Cancel any ongoing getChallengesWithProgress request when navigating to this view
    cancelGetChallengesWithProgress()
    // Then load the single challenge
    getCurrentCompanyGoal({ id: goalId, token })
  }, [goalId, token, getCurrentCompanyGoal, cancelGetChallengesWithProgress])

  // App state change listener
  useAppStateListener(goalId, token, getCurrentCompanyGoal)

  // Sync progress for compact ranking view.
  // Must be above the early return so hooks are always called in the same order.
  // Use a ref for the goal object to avoid re-triggering when the goal reference
  // changes (e.g. after refreshChallengeResults updates the store).
  const goalRef = React.useRef(goal)
  goalRef.current = goal

  React.useEffect(() => {
    if (!goal?.compactRankingView) return

    const doSync = () => {
      console.log('YumCustomEntry:syncProgress | trigger refreshChallengeResults | isLegacyRanking:', isLegacyRanking)
      syncChallengeProgress({ goal: goalRef.current!, token, isLegacyRanking })
    }

    doSync()

    // Listen for app returning to foreground and re-sync
    const listenerPromise = App.addListener('appStateChange', async ({ isActive }) => {
      if (isActive) {
        doSync()
      }
    })

    // Clean up the listener on unmount or when deps change
    return () => {
      listenerPromise.then((handle) => handle.remove()).catch(() => {})
    }
  }, [goal?.compactRankingView, goal?.id, token, isLegacyRanking])

  if (!goal) return null

  if (isLegacyRanking && goal.splitRankingView) {
    console.log(`Redirecting to new leaderboard view`)
    store.dispatch(push(`/challenges/${goalId}/leaderboard`))
  }

  /*if (!isLegacyRanking && !goal.compactRankingView) {
    console.log(`Redirecting to old ranking view`)
    store.dispatch(push(`/challenges/${goalId}/ranking`))
  }*/

  if (!isChallengeTypeSupported(goal)) {
    return <UnsupportedChallengeMessage />
  }

  return (
    <div style={isLegacyRanking ? { paddingBottom: '24px' } : {}}>
      <NotificationsWrapper />
      {/* Progress celebration for native apps */}
      {
        /*isNative() &&*/ activityView === ActivityView.INDIVIDUAL && goal ? (
          <YumUserProgressCelebration goal={goal} />
        ) : null
      }

      {/* Custom entry for legacy ranking */}
      {(isLegacyRanking || token) && !goal.compactRankingView ? (
        <YumCustomEntryWrapper
          goal={goal}
          showManualEntry={true}
          isMeView={false}
          token={token}
          isLegacyRanking={isLegacyRanking}
        />
      ) : null}
      {/* Goal ranking */}
      <YumGoalRankingWrapper goalDto={goal} isLegacyRanking={isLegacyRanking} token={token} />
    </div>
  )
}
